import React from 'react';
import {
  View, StyleSheet, Text, FlexStyle,
} from 'react-native';
import { CartesianChart, Line } from 'victory-native';
import { Circle, useFont } from '@shopify/react-native-skia';
import Colors from '../constants/Colors';
import {
  axisDateLabel,
  ChartDataPoint,
} from './axisUtils';
import { selectedLineColor } from './AirQualityGraph';
import { comparisonLineColor } from '../components/Home/HomeGraphLineSelector';

interface LineGraphMobileProps {
  data: ChartDataPoint[];
  comparisonData: ChartDataPoint[];
  style?: FlexStyle | FlexStyle[];
}

const LineGraphMobile = ({ data, comparisonData, style }: LineGraphMobileProps) => {
  const dataExists: boolean = !!(data?.length || comparisonData?.length);
  // eslint-disable-next-line global-require
  const font = useFont(require('../assets/fonts/Roboto-Regular.ttf'), 14);

  const domainLimit: number = dataExists
    ? 1.2
      * Math.max(
        ...(data ?? []).map((point) => point.value),
        ...(comparisonData ?? []).map((point) => point.value),
        1,
      )
    : 1;

  return dataExists ? (
    <View style={[styles.container, style]}>
      <CartesianChart
        data={data}
        xKey="date"
        yKeys={['value']}
        domain={{ y: [0, domainLimit] }}
        frame={{ lineWidth: 0 }}
        axisOptions={{
          font,
          tickCount: { x: 3, y: 4 },
          labelOffset: { x: 0, y: 6 },
          formatXLabel: (label: string) => {
            // Try to find the index of the label in the data array
            const idx = data.findIndex((point) => String(point.date) === label);
            return axisDateLabel(data, idx >= 0 ? idx : 0);
          },
          lineColor: Colors.light.hereworksWhite500,
          lineWidth: { grid: { x: 0, y: 1 }, frame: 0 },
        }}
        padding={{
          top: 50, right: 10, bottom: 10, left: 30,
        }}
        domainPadding={{ top: 50, left: 0, bottom: 20 }}
      >
        {({ points }) => (
          <>
            <Line
              points={points.value}
              color={selectedLineColor}
              strokeWidth={2}
              animate={{ type: 'timing', duration: 300 }}
            />
            {points.value.map((point, idx) => (
              <Circle
                key={idx}
                cx={point.x}
                cy={point.y}
                r={3}
                color={selectedLineColor}
              />
            ))}
            {comparisonData?.length > 0 && (
              <Line
                points={comparisonData.map((point) => ({
                  x: typeof point.date === 'number' ? point.date : new Date(point.date).getTime(),
                  xValue: point.date,
                  y: point.value,
                  yValue: point.value,
                }))}
                color={comparisonLineColor}
                strokeWidth={2}
              />
            )}
          </>
        )}
      </CartesianChart>
    </View>
  ) : (
    <View style={[{ justifyContent: 'center', alignItems: 'center', flex: 1 }]}>
      <Text>{'No data available'}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: -40,
    marginEnd: 0,
  },
});

export default LineGraphMobile;
